import { useEffect, useState, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftBadgeDot from "components/SoftBadgeDot";
import SoftSelect from "components/SoftSelect";
import SoftTypography from "components/SoftTypography";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";
import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";
import ComplexReportsDoughnutChart from "examples/Charts/DoughnutCharts/ComplexReportsDoughnutChart";

import Isoweeks from "layouts/pages/analytics/components/Isoweeks";
import Pages from "layouts/pages/analytics/components/Pages";

import getEIVsChartData from "layouts/pages/analytics/data/defaultLineChartData";
import getComplexReportsDoughnutChartData from "layouts/pages/analytics/data/complexReportsDoughnutChartData";
import { fetchWeeklyMetrics } from "layouts/pages/analytics/data/defaultLineChartData";


import AuthApi from "api/auth";
import EIVAPI from "api/eiv";

function Analytics() {
  const [menu, setMenu] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [curFacility, setFacility] = useState(null);
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState("client");
  const [lastAvailableWeek, setLastAvailableWeek] = useState(null);
  const [patientStats, setPatientStats] = useState({
    count: 0,
    percentageChange: 0,
  });
  const [claimsStats, setClaimsStats] = useState({ count: 0, percentageChange: 0 });
  const [beforeded, setbeforeded] = useState({
    count: 0,
    percentageChange: 0,
  });
  const [afterded, setafterded] = useState({
    count: 0,
    percentageChange: 0,
  });
  const [doughnutData, setDoughnutData] = useState(null);


  const openMenu = (event) => setMenu(event.currentTarget);
  const closeMenu = () => setMenu(null);

  useEffect(() => {
    const fetchUser = async () => {
      const userFinded = await AuthApi.Me();
      setUser(userFinded.data);
      setUserRole(userFinded.data.role);
  
      if (userFinded.data.role === "client") {
        setFacility(userFinded.data.facility.Facility);
      } else if (userFinded.data.role === "admin") {
        setFacility("LHNJ");
      }
    };
  
    fetchUser();
  }, []);
  useEffect(() => {
    if (!curFacility) return;

    const fetchChart = async () => {
      const lastWeekRes = await EIVAPI.GetLastWeekForFacility(curFacility);
      setLastAvailableWeek(lastWeekRes.data);

      const data = await getEIVsChartData(curFacility);
      setChartData(data);
    };

    fetchChart();
  }, [curFacility]);
  
  useEffect(() => {
    const fetchPatientStats = async () => {
      if (!lastAvailableWeek || !curFacility) return;
  
      const { week, year } = lastAvailableWeek;
      const previousWeek = week > 1 ? week - 1 : 52;
      const previousYear = week > 1 ? year : year - 1;
  
      try {
        const current = await fetchWeeklyMetrics(curFacility, week, year);
        const previous = await fetchWeeklyMetrics(curFacility, previousWeek, previousYear);
  
        const currentPatients = parseInt(current?.unique_patients || 0);
        const previousPatients = parseInt(previous?.unique_patients || 0);
  
        const currentClaims = parseInt(current?.claims_count || 0);
        const previousClaims = parseInt(previous?.claims_count || 0);

        const currentbeforeded = parseFloat(current?.beforededpercentage *100|| 0);
        const previousbeforeded = parseFloat(previous?.beforededpercentage *100|| 0);

        const currentaferded = parseFloat(current?.afterdedpercentage *100|| 0);
        const previousafterded = parseFloat(previous?.afterdedpercentage *100|| 0);
  
        console.log("📊 Patients this week:", currentPatients);
        console.log("📉 Patients previous week:", previousPatients);
  
        console.log("📄 Claims this week:", currentClaims);
        console.log("📃 Claims previous week:", previousClaims);
  
        const patientPercentageChange = previousPatients
          ? ((currentPatients - previousPatients) / previousPatients) * 100
          : 0;
  
        const claimsPercentageChange = previousClaims
          ? ((currentClaims - previousClaims) / previousClaims) * 100
          : 0;

        const beforededPercentageChange = previousbeforeded
          ? (currentbeforeded - previousbeforeded) 
          : 0;

        const afterdedPercentageChange = previousafterded
          ? (currentaferded - previousafterded) 
          : 0;
  
        setPatientStats({
          count: currentPatients,
          percentageChange: patientPercentageChange,
        });
  
        setClaimsStats({
          count: currentClaims,
          percentageChange: claimsPercentageChange,
        });

        setbeforeded({
          count: currentbeforeded,
          percentageChange: beforededPercentageChange,
        });

        setafterded({
          count: currentaferded,
          percentageChange: afterdedPercentageChange,
        });
      } catch (err) {
        console.warn("Error fetching patient/claims stats", err);
      }
    };
  
    fetchPatientStats();
  }, [lastAvailableWeek, curFacility]);
  
  useEffect(() => {
    const fetchDoughnut = async () => {
      if (!curFacility || !lastAvailableWeek) return;
      const { week, year } = lastAvailableWeek;
      const chartData = await getComplexReportsDoughnutChartData(curFacility, week, year);
      setDoughnutData(chartData);
    };
  
    fetchDoughnut();
  }, [curFacility, lastAvailableWeek]);
  

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox display="flex" justifyContent="space-between" mb={3} ml={2}>
          <SoftBox display="flex" alignItems="center" minHeight="40px">
            {userRole === "client" ? (
              <SoftTypography variant="h6" fontWeight="medium">
                {curFacility}
              </SoftTypography>
            ) : (
              <SoftSelect
                defaultValue={{ value: curFacility, label: curFacility }}
                options={[
                  { value: "LHNJ", label: "LHNJ" },
                  { value: "LHNNJ", label: "LHNNJ" },
                  { value: "LHCC", label: "LHCC" },
                  { value: "LHCM", label: "LHCM" },
                  { value: "LHCO", label: "LHCO" },
                  { value: "WRC", label: "WRC" },
                  { value: "NSR", label: "NSR" },
                  { value: "THI", label: "THI" },
                  { value: "SERC", label: "SERC" },
                  { value: "EH", label: "EH" },
                  { value: "CDR", label: "CDR" },
                ]}
                onChange={(e) => setFacility(e.value)}
                size="small"
              />
            )}
          </SoftBox>
          <SoftBox>
            <SoftButton variant="gradient" color="dark" onClick={openMenu}>
              {lastAvailableWeek ? `Week ${lastAvailableWeek.week}, ${lastAvailableWeek.year}` : "today"}&nbsp;
              <Icon>expand_more</Icon>
            </SoftButton>
          </SoftBox>
        </SoftBox>

        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} lg={3}>
              <MiniStatisticsCard
                title={{ text: "patients", fontWeight: "medium" }}
                count={patientStats.count.toString()}
                percentage={{
                  color: patientStats.percentageChange > 0 ? "success" : "error",
                  text: `${typeof beforeded.percentageChange === "number"
                    ? (beforeded.percentageChange > 0 ? "+" : "") + beforeded.percentageChange.toFixed(2)
                    : "0.00"}%`,
                }}
                icon={{ color: "dark", component: "account_circle" }}
              />
            </Grid>         
            
            <Grid item xs={12} sm={6} lg={3}>
              <MiniStatisticsCard
                title={{ text: "claims", fontWeight: "medium" }}
                count={claimsStats.count}
                percentage={{
                  color: claimsStats.percentageChange > 0 ? "success" : claimsStats.percentageChange < 0 ? "error" : "dark",
                  text: `${claimsStats.percentageChange > 0 ? "+" : ""}${claimsStats.percentageChange.toFixed(2)}%`,
                }}
                icon={{ color: "dark", component: "assignment" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <MiniStatisticsCard
                title={{ text: "Before Dedictible %", fontWeight: "medium" }}
                count={`${beforeded.count.toFixed(2)}%`}
                percentage={{
                  color: beforeded.percentageChange > 0 ? "success" : beforeded.percentageChange < 0 ? "error" : "dark",
                  text: `${beforeded.percentageChange > 0 ? "+" : ""}${beforeded.percentageChange.toFixed(2)}%`,
                }}
                icon={{ color: "dark", component: "assignment" }}
              />
            </Grid>
            {afterded.count > 0 && (
            <Grid item xs={12} sm={6} lg={3}>
              <MiniStatisticsCard
                title={{ text: "After Deductible %", fontWeight: "medium" }}
                count={`${afterded.count.toFixed(2)}%`}
                percentage={{
                  color:
                    afterded.percentageChange > 0
                      ? "success"
                      : afterded.percentageChange < 0
                      ? "error"
                      : "dark",
                  text: `${
                    afterded.percentageChange > 0 ? "+" : ""
                  }${afterded.percentageChange.toFixed(2)}%`,
                }}
                icon={{ color: "dark", component: "assignment" }}
              />
            </Grid>
          )}
            
          </Grid>
        </SoftBox>

        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={6}>
              {chartData ? (
                <DefaultLineChart
                  title="EIVs"
                  description={
                    <SoftBox display="flex" ml={-1}>
                      <SoftBadgeDot color="info" size="sm" badgeContent="Billed" />
                      <SoftBadgeDot color="dark" size="sm" badgeContent="Before DED" />
                      <SoftBadgeDot color="primary" size="sm" badgeContent="After DED" />
                    </SoftBox>
                  }
                  chart={chartData}
                />
              ) : (
                <div>Loading EIV chart...</div>
              )}
            </Grid>

            <Grid item xs={12} lg={6}>
              {doughnutData ? (
                <ComplexReportsDoughnutChart
                  title="Last week billing"
                  chart={doughnutData}
                  tooltip="See the percentage each insurance company has in the last week billing."
                  action={{
                    type: "internal",
                    route: "/dashboards/eivs",
                    color: "secondary",
                    label: "see all EIVs",
                  }}
                />
              ) : (
                <SoftBox p={2}>
                  <SoftTypography variant="button">Loading billing chart...</SoftTypography>
                </SoftBox>
              )}
            </Grid>
          </Grid>
        </SoftBox>

        <Grid container spacing={6}>
        <Grid item xs={12} lg={5}>
          <Isoweeks lastAvailableWeek={lastAvailableWeek} />
        </Grid>
          
        </Grid>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Analytics;